"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import type { WILCOXON_SIGNED_RANK_TEST_CALCULATOR_STRINGS } from "@/components/tools/wilcoxon-signed-rank-test-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import {
  EXACT_LIMIT,
  type WilcoxonResult,
} from "@/lib/tool-kit/stats-tests/wilcoxon";

/** Results for the Wilcoxon signed-rank calculator. */

type Strings = typeof WILCOXON_SIGNED_RANK_TEST_CALCULATOR_STRINGS;

const MAX_ROWS = 40;

function methodNote(
  r: WilcoxonResult,
  continuity: boolean,
  strings: Strings,
  fmt: NumberFormatter
): string {
  if (r.method === "exact") {
    return t(strings.methodExact, { p: formatP(r.pNormal, fmt) });
  }
  if (!r.hasTies && r.n > EXACT_LIMIT) {
    return continuity ? strings.methodSizeCc : strings.methodSize;
  }
  return continuity ? strings.methodTiesCc : strings.methodTies;
}

function RankRows({
  r,
  strings,
  fmt,
}: {
  r: WilcoxonResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (r.rows.length > MAX_ROWS) {
    return <p className={HINT}>{strings.ranksHidden}</p>;
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.ranksHeading}</h3>
      <p className={HINT}>{strings.ranksHint}</p>
      <ResultTable
        head={[
          strings.pair,
          strings.difference,
          strings.absolute,
          strings.signedRank,
        ]}
        rows={r.rows.map((row, i) => {
          let signed: string = strings.dropped;
          if (row.rank !== null) {
            signed = formatStat(
              row.difference > 0 ? row.rank : -row.rank,
              fmt,
              1
            );
          }
          return {
            key: `pair-${i + 1}`,
            cells: [
              fmt(i + 1),
              formatStat(row.difference, fmt),
              formatStat(Math.abs(row.difference), fmt),
              signed,
            ],
          };
        })}
      />
    </>
  );
}

export function WilcoxonResults({
  r,
  continuity,
  alpha,
  strings,
  locale,
  fmt,
}: {
  r: WilcoxonResult;
  continuity: boolean;
  alpha: number;
  strings: Strings;
  locale: string;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const raw = (r.tPlus - r.mu) / r.sigma;
  const apa = richTemplate(strings.apaTemplate, {
    t: formatStat(r.w, fmt, 1),
    z: fixed(r.z, 2, fmt),
    p: apaP(r.p, fmt),
    r: fixed(r.r, 2, fmt, false),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.tStatistic}
          value={formatStat(r.w, fmt, 1)}
        />
        <StatCard
          emphasis
          label={r.method === "exact" ? strings.pExact : strings.pNormal}
          value={formatP(r.p, fmt)}
        />
        <StatCard label={strings.tPlus} value={formatStat(r.tPlus, fmt, 1)} />
        <StatCard label={strings.tMinus} value={formatStat(r.tMinus, fmt, 1)} />
        <StatCard label={strings.zScore} value={n(r.z)} />
        <StatCard label={strings.effectR} value={n(r.r)} />
        <StatCard label={strings.rankBiserial} value={n(r.rankBiserial)} />
        <StatCard
          label={strings.pairsUsed}
          value={`${fmt(r.n)} / ${fmt(r.nTotal)}`}
        />
      </StatGrid>
      <p className={HINT}>{methodNote(r, continuity, strings, fmt)}</p>
      {r.zeros > 0 ? (
        <p className={HINT}>
          {tPlural(strings.zeros, locale, r.zeros, { count: fmt(r.zeros) })}{" "}
          {strings.zerosNote}
        </p>
      ) : null}
      <Decision reject={r.p < alpha}>
        {t(r.p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <RankRows fmt={fmt} r={r} strings={strings} />
      <Working heading={strings.howCalculated}>
        <p>
          T⁺ = {formatStat(r.tPlus, fmt, 1)}, T⁻ ={" "}
          {formatStat(r.tMinus, fmt, 1)}, n = {fmt(r.n)}; T⁺ + T⁻ = n(n + 1)/2 ={" "}
          {fmt((r.n * (r.n + 1)) / 2)}
        </p>
        <p>
          μ = n(n + 1)/4 = {n(r.mu)}; σ = √(n(n + 1)(2n + 1)/24 − Σ(t³ − t)/48)
          = {n(r.sigma)}
        </p>
        <p>
          z = (T⁺ − μ) / σ = ({formatStat(r.tPlus, fmt, 1)} − {n(r.mu)}) /{" "}
          {n(r.sigma)} = {n(raw)}
        </p>
        {r.z === raw ? null : (
          <p>{t(strings.workingContinuity, { z: n(r.z) })}</p>
        )}
        <p>
          r = |z| / √n = {n(Math.abs(r.z))} / √{fmt(r.n)} = {n(r.r)}; r
          <sub>rb</sub> = (T⁺ − T⁻) / (T⁺ + T⁻) = {n(r.rankBiserial)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={
          r.method === "exact" ? strings.apaNoteExact : strings.apaNoteNormal
        }
        parts={apa}
      />
    </section>
  );
}
